import { useEffect, useState } from "react";
import { Link, useParams, Navigate, useLocation } from "react-router-dom";
import { api, fileUrl, resolveMediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/AuthProvider";
import { Button } from "@/components/ui/button";
import ProfileCompletion from "@/components/ProfileCompletion";
import { Loader2, Lock, EyeOff, UserPlus, UserMinus } from "lucide-react";
import { toast } from "sonner";

function resolveUsername(params, pathname) {
  // /u/:username route → params.username is set
  if (params.username) return decodeURIComponent(params.username);
  // Catch-all path="*" → params['*'] is the full matched path e.g. "@bob"
  if (params["*"]) {
    const raw = decodeURIComponent(params["*"]).split("/")[0];
    return raw.replace(/^@/, "");
  }
  // Last-ditch: parse from pathname (handles /@bob and /%40bob)
  const m = pathname.match(/^\/(?:@|%40)([^/?#]+)/i);
  return m ? decodeURIComponent(m[1]) : null;
}

export default function PublicProfile() {
  const params = useParams();
  const { pathname } = useLocation();
  const username = resolveUsername(params, pathname);
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [follow, setFollow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // If the catch-all caught a path that isn't a username profile, send the user home
    if (!username || (!pathname.startsWith("/@") && !pathname.startsWith("/%40") && !pathname.startsWith("/u/"))) {
      setError("notfound");
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      api.get(`/users/by-username/${encodeURIComponent(username)}`),
      api.get(`/users/${encodeURIComponent(username)}/follow-state`).catch(() => ({ data: null })),
    ]).then(([{ data: profile }, { data: fs }]) => {
      setData(profile);
      setFollow(fs);
    }).catch((e) => setError(e.response?.status === 403 ? "private" : "notfound"))
      .finally(() => setLoading(false));
  }, [username, pathname]);

  const toggleFollow = async () => {
    setBusy(true);
    try {
      const { data: fs } = follow?.following
        ? await api.delete(`/users/${username}/follow`)
        : await api.post(`/users/${username}/follow`, {});
      setFollow((s) => ({ ...s, ...fs }));
      toast.success(fs.following ? `Following @${username}` : `Unfollowed @${username}`);
    } catch (e) { toast.error("Could not update follow"); }
    finally { setBusy(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>
  );
  if (error === "notfound") return <Navigate to="/home" replace />;

  const p = data?.profile;
  const isOwner = data?.is_owner;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <div className="border border-border p-8 flex flex-col md:flex-row md:items-center gap-6">
        {isOwner ? (
          <ProfileCompletion percent={p.completion_percent || 0} picture={p.picture} name={p.name} size={120} showLabel />
        ) : (
          <div className="h-28 w-28 border border-border overflow-hidden rounded-full shrink-0">
            {p?.picture && <img src={resolveMediaUrl(p.picture)} alt="" className="h-full w-full object-cover" />}
          </div>
        )}
        <div className="flex-1">
          <p className="eyebrow">@{p?.username}</p>
          <h1 className="font-serif text-5xl tracking-tight mt-1">{p?.name}</h1>
          {p?.bio && <p className="text-sm text-muted-foreground mt-3 max-w-lg">{p.bio}</p>}
          {p?.style_dna?.style_personality && (
            <p className="mt-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Style · {p.style_dna.style_personality}
            </p>
          )}
          {follow && (
            <div className="mt-4 flex gap-5 text-sm">
              <span data-testid="follower-count"><b>{follow.followers_count}</b> <span className="text-muted-foreground text-[10px] uppercase tracking-[0.2em]">followers</span></span>
              <span data-testid="following-count"><b>{follow.following_count}</b> <span className="text-muted-foreground text-[10px] uppercase tracking-[0.2em]">following</span></span>
              <span><b>{data?.posts?.length || 0}</b> <span className="text-muted-foreground text-[10px] uppercase tracking-[0.2em]">posts</span></span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {isOwner ? (
            <Link to="/profile">
              <Button variant="outline" data-testid="public-edit-btn" className="rounded-none uppercase tracking-[0.2em] text-xs">Edit profile</Button>
            </Link>
          ) : (
            <Button
              data-testid="public-follow-btn"
              onClick={toggleFollow}
              disabled={busy}
              variant={follow?.following ? "outline" : "default"}
              className="rounded-none uppercase tracking-[0.2em] text-xs h-11">
              {busy ? <Loader2 size={14} className="animate-spin" /> :
               follow?.following ? <><UserMinus size={12} className="mr-2" /> Following</> :
                                   <><UserPlus size={12} className="mr-2" /> Follow</>}
            </Button>
          )}
        </div>
      </div>

      {error === "private" || p?.privacy === "private" ? (
        <div className="mt-10 border border-dashed border-border p-16 text-center">
          <Lock size={28} className="mx-auto" />
          <p className="font-serif text-3xl mt-4">This profile is private.</p>
        </div>
      ) : (
        <div className="mt-10">
          <h2 className="editorial-h2">Shared looks</h2>
          {!data?.posts?.length ? (
            <div className="border border-dashed border-border p-12 mt-6 text-center">
              <EyeOff size={20} className="mx-auto" />
              <p className="font-serif text-2xl mt-3">No posts yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
              {data.posts.map((post) => (
                <div key={post.id} className="border border-border bg-card">
                  {post.image_path && (
                    <img src={fileUrl(post.image_path)} alt="" className="w-full aspect-square object-cover" />
                  )}
                  <p className="p-3 text-sm">{post.caption}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
